import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { setCanonical, setMeta } from '@/utils/seo';
import { FAQSchema, BreadcrumbSchema, SpeakableSchema, ServiceSchema } from '@/components/SEOSchemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuardianCountryPage() {
  const { slug } = useParams();
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/countries/${slug}`).then(r => {
      setCountry(r.data);
      setCanonical(`/guardian/${slug}`);
      setMeta(`Guardian Mandate for OFW Families in ${r.data.name} | LegaSecure Guide`, `Set up a guardian mandate for your children from ${r.data.name}. Protect your family if something happens while working abroad.`);
    }).catch(() => setCountry(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="ls-container py-20 text-center"><i className="fa-solid fa-spinner fa-spin text-2xl text-[var(--ls-navy)]"></i></div>;
  if (!country) return <div className="ls-container py-20 text-center"><h1 className="text-2xl font-bold">Country not found</h1></div>;

  const faqs = [
    { question: `How do I set up a guardian mandate from ${country.name}?`, answer: `With LegaSecure, you can create a guardian mandate digitally from ${country.name}. Simply log in, designate your guardian, enter care instructions, and store everything in the encrypted vault.` },
    { question: `Who should I designate as guardian?`, answer: `Choose someone you trust completely - usually a close family member in the Philippines who is responsible, available, and willing to care for your children.` },
    { question: `What information should I include?`, answer: `Include your children's medical history, allergies, school information, daily routines, emergency contacts, and financial access details.` },
  ];

  return (
    <div data-testid="guardian-country-page">
      <FAQSchema faqs={faqs} />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'OFW Countries', url: '/all-countries' }, { name: country.name, url: `/ofw/${slug}` }, { name: 'Guardian Mandate' }]} />
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />
      <ServiceSchema />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1577897113292-3b95936e5206?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/80"></div>
        </div>
        <div className="ls-container relative z-10 py-12">
          <nav className="text-sm text-[var(--ls-text-light)] mb-4">
            <Link to="/" className="hover:text-[var(--ls-gold)]">Home</Link> / <Link to="/all-countries" className="hover:text-[var(--ls-gold)]">OFW Countries</Link> / <Link to={`/ofw/${slug}`} className="hover:text-[var(--ls-gold)]">{country.name}</Link> / <span className="text-[var(--ls-navy)] font-medium">Guardian Mandate</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold font-['Crimson_Pro'] mb-3" style={{ color: '#1B365D' }}>Guardian Mandate for OFW Families in {country.name}</h1>
          <p className="speakable-intro text-lg max-w-2xl" style={{ color: '#4A5568' }}>
            Working in {country.name} means being far from your children. A guardian mandate ensures they are always protected — with clear instructions for whoever cares for them.
          </p>
          <a href="https://legasecure.com" className="ls-btn-navy mt-6 inline-block">Designate Your Guardian</a>
        </div>
      </section>

      <section className="ls-section">
        <div className="ls-container max-w-4xl">
          <div className="ls-card bg-amber-50 border border-amber-200 mb-8">
            <p className="text-sm text-amber-800"><i className="fa-solid fa-triangle-exclamation mr-2"></i><strong>Real Scenario:</strong> You're in {country.name} and get in an accident. Your children in the Philippines need someone to take them to school, refill prescriptions, and access your bank account for their tuition. <strong>Without a guardian mandate, no one has authority to do this.</strong></p>
          </div>

          <div className="ls-card prose-content mb-8">
            <h2>What is a Guardian Mandate?</h2>
            <p>A guardian mandate is your backup plan for your children. It legally designates who cares for them and provides detailed instructions for their wellbeing.</p>
            <h2>What Your Guardian Gets Access To</h2>
            <ul>
              <li><strong>Care Instructions:</strong> Daily routines, dietary needs, bedtime schedules</li>
              <li><strong>Medical Records:</strong> Allergies, medications, doctor contacts, insurance</li>
              <li><strong>School Information:</strong> School records, passwords, teacher contacts</li>
              <li><strong>Financial Access:</strong> Bank accounts, emergency funds for children's needs</li>
              <li><strong>Emergency Contacts:</strong> Extended family, neighbors, trusted friends</li>
            </ul>
            <h2>Why OFWs in {country.name} Need This</h2>
            <p>With {country.ofw_estimate} Filipinos in {country.name}, emergencies happen. Distance means delays. A guardian mandate eliminates the chaos and ensures your children are cared for immediately.</p>
          </div>

          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((f, i) => <FAQItem key={i} question={f.question} answer={f.answer} />)}
            </div>
          </div>

          <div className="flex gap-4 flex-wrap">
            <Link to={`/ofw/${slug}`} className="ls-btn-outline text-sm">Back to {country.name} Guide</Link>
            <Link to="/guides/guardian-mandate-filipino-children" className="ls-btn-outline text-sm">Full Guardian Mandate Guide</Link>
          </div>
        </div>
      </section>

      <section className="bg-[var(--ls-bg)] py-12">
        <div className="ls-container text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Protect Your Children from {country.name}</h2>
          <a href="https://legasecure.com" className="ls-btn-gold text-lg px-8 py-3">Set Up Guardian Mandate <i className="fa-solid fa-arrow-right ml-2"></i></a>
        </div>
      </section>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ls-card cursor-pointer" onClick={() => setOpen(!open)}>
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-sm text-[var(--ls-navy)] pr-4">{question}</h3>
        <i className={`fa-solid fa-chevron-down text-[var(--ls-text-light)] transition-transform text-xs ${open ? 'rotate-180' : ''}`}></i>
      </div>
      {open && <p className="mt-2 text-sm text-[var(--ls-text-light)] leading-relaxed">{answer}</p>}
    </div>
  );
}
